import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, KundeAkte, KundeListe, KonzernKnoten,
              Unternehmen } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { KundenListe } from "./KundenListe.js";
import "./KundeKonzern.css";

/**
 * Der Reiter „KONZERN" (Spec „Adressen und Konzernstruktur", 10.09.2026, Etappe 2):
 * Elternkette nach oben, Töchter nach unten, Sprung in jede Akte — und der einzige Ort, an
 * dem der übergeordnete Kunde gesetzt und entfernt wird.
 *
 * **Keine Summen** (Nicht-Ziel der Spec). Der Reiter ordnet ein, er rechnet nicht.
 *
 * **Die Kundenwahl ist die BEKANNTE Kundenliste** — derselbe Baustein, den der
 * Angebots-Bereich vor sein Anlegen setzt (`BereichAngebote.tsx`, Abschnitt „Kunde
 * wählen"). Ein zweiter Sucher mit eigener Verzögerung, eigenen Kürzel-Chips und eigenem
 * Leerzustand wäre eine zweite Fassung derselben Liste; und der Bearbeiter sucht einen
 * Kunden auf jedem Bildschirm gleich.
 *
 * **Die REIHENFOLGE kommt vom Server** (Aufgabe 3): die Kette mit dem nächsten Vorfahr
 * zuerst — er ist der, von dem geerbt wird —, bei den Töchtern die archivierten voran
 * (Hausregel „Schlechtestes oben"). Hier wird nicht nachsortiert: Eine zweite
 * Sortierregel in der Oberfläche wäre eine, die eines Tages anders urteilt als die erste.
 *
 * **Eigener CSS-Block `kkz__`** — kein geteilter Blockname über Ansichten hinweg.
 */

/** Nur die drei Klientenmethoden, die dieser Reiter braucht — so lässt er sich im Test
 *  mit drei `vi.fn` zeichnen (Muster KundePortalMandanten/AdressListe). */
export type KonzernClient = Pick<ApiClient,
  "getKonzern" | "setCustomerParent" | "getCustomers">;

export type KundeKonzernProps = {
  client: KonzernClient;
  /** Die Akte selbst — für Kennung, Name, Version und den aktuellen Elternteil. */
  kunde: KundeAkte;
  /** Sprung in eine andere Akte (Kette, Töchter). */
  onKunde: (id: string) => void;
  /**
   * Nach jeder Änderung: der FRISCHE Kundensatz, den der Schreibweg zurückgibt.
   *
   * Hier ein Satz und kein blosser Ruf „hol die Akte neu" (wie beim Adressen-Reiter): Der
   * Server antwortet auf `PATCH .../parent` mit der ganzen Akte, samt neuer `version` und
   * neuem `parent`. Sie noch einmal abzuholen wäre ein zweiter Weg für dieselbe Auskunft —
   * und die Akte stünde in der Zwischenzeit mit einer Version da, die es nicht mehr gibt.
   */
  onAktualisiert: (akte: KundeAkte) => void;
  /** Für die Kürzel-Chips in der Kundenwahl. */
  companies?: Unternehmen[];
};

/** Unter zwei Zeichen wird nicht gesucht — dieselbe Grenze wie in den Bereichslisten. */
const SUCH_MINDESTLAENGE = 2;

/**
 * `conflict.current` kommt als `unknown` über die Leitung (die API kann seine Gestalt im
 * Schema nicht versprechen). Tatsächlich liefert der Fehlerhandler den vollen Kundensatz;
 * hier wird das geprüft statt blind behauptet — dasselbe Vorgehen wie in KundeStammdaten.
 */
function alsAkte(wert: unknown): KundeAkte | null {
  if (typeof wert !== "object" || wert === null) return null;
  const k = wert as Partial<KundeAkte>;
  if (typeof k.id !== "string" || typeof k.companyName !== "string"
    || typeof k.version !== "number") return null;
  return wert as KundeAkte;
}

export function KundeKonzern(p: KundeKonzernProps): ReactElement {
  /** `null` heisst „noch nicht geladen" — von „geladen und leer" zu unterscheiden. */
  const [kette, setKette] = useState<KonzernKnoten[] | null>(null);
  const [toechter, setToechter] = useState<KonzernKnoten[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  const [panelOffen, setPanelOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<KundeListe | null>(null);
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const [sucheLaeuft, setSucheLaeuft] = useState(false);
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  /** Hochgezählt von „Erneut versuchen" — wirft den Lade-Effekt noch einmal an, ohne dass
   *  der Kunde wechselt (dieselbe Naht wie `akteVersion` in App.tsx). */
  const [ladeVersuch, setLadeVersuch] = useState(0);

  /** Der Knopf, der nach dem Entfernen den Fokus bekommt: Der Knopf, der ihn hatte, ist
   *  dann fort, und ohne dieses Ziel fiele der Fokus auf den Seitenrumpf zurück. */
  const waehlenRef = useRef<HTMLButtonElement>(null);
  /** Nach einem erfolgreichen Entfernen einmal den Fokus setzen (siehe Effekt unten). */
  const fokusNachEntfernen = useRef(false);

  /**
   * Die laufende NUMMER dieses Reiters — der Riegel gegen Antworten, die zu spät kommen
   * (Muster `KundePortalMandanten`/`AdressListe`). Der Kundenwechsel und das Abräumen
   * zählen sie hoch und machen ALLE fliegenden Antworten ungültig. Hier zählt das doppelt:
   * Der Reiter führt selbst in eine andere Akte, und die Antwort auf die Kette des ALTEN
   * Kunden darf dort nicht mehr landen.
   */
  const lauf = useRef(0);
  const { client, kunde } = p;
  const kundeId = kunde.id;
  const getKonzern = client.getKonzern;
  const getCustomers = client.getCustomers;

  /*
   * Laden — und zugleich der vollständige Rückbau beim Kundenwechsel. KEIN ZUSTAND
   * ÜBERLEBT DEN KONTEXTWECHSEL (Hausregel): Kette, Töchter, ein offenes Panel, die Suche
   * darin und jede Meldung fallen weg. Die Akte setzt zwar zusätzlich einen `key` am
   * Kunden (KundenAkte.tsx) — aber ein Baustein, der sich nur auf den Aufrufer verlässt,
   * ist an seinem zweiten Einbauort still kaputt.
   */
  useEffect(() => {
    const meiner = ++lauf.current;
    const nochAktuell = (): boolean => meiner === lauf.current;
    setKette(null);
    setToechter(null);
    setLadeFehler(null);
    setMeldung(null);
    setPanelOffen(false);
    setGewaehlt(null);
    setSuche("");
    setTreffer([]);
    setFeldFehler(null);
    // Auch der Schreib-Riegel fällt: Wechselt die Akte, während ein PATCH unterwegs ist,
    // gehört seine Antwort dem alten Kunden — stünde `arbeitet` weiter auf wahr, blieben
    // hier beide Ausgänge für immer gesperrt.
    setArbeitet(false);
    void (async () => {
      try {
        const sicht = await getKonzern(kundeId);
        if (!nochAktuell()) return;
        setKette(sicht.kette);
        setToechter(sicht.toechter);
      } catch (fehler) {
        if (!nochAktuell()) return;
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Der Konzern konnte nicht geladen werden.");
      }
    })();
    return () => { lauf.current += 1; };
  }, [getKonzern, kundeId, ladeVersuch]);

  /*
   * Der Fokus nach dem Entfernen. Er wird HIER gesetzt und nicht im Schreibweg: Der Knopf
   * „Übergeordneten Kunden entfernen" verschwindet mit der Zuordnung, und erst wenn der
   * neue Kundensatz gezeichnet ist, steht sein Nachbar als Fokusziel bereit.
   */
  useEffect(() => {
    if (!fokusNachEntfernen.current) return;
    fokusNachEntfernen.current = false;
    waehlenRef.current?.focus();
  });

  /** Die Suche im Panel — die Verzögerung bringt `KundenListe` selbst mit; hier wird nur
   *  der abgesetzte Suchbegriff beantwortet. */
  useEffect(() => {
    if (!panelOffen) return;
    if (suche.trim().length < SUCH_MINDESTLAENGE) { setTreffer([]); return; }
    const meiner = lauf.current;
    setSucheLaeuft(true);
    void (async () => {
      try {
        const { items } = await getCustomers({ search: suche.trim(), limit: 20 });
        if (meiner !== lauf.current) return;
        /*
         * Was der Server mit 422 abwiese, steht gar nicht erst zur Wahl: der KUNDE SELBST
         * und seine unmittelbaren TÖCHTER (beide ergäben einen Kreis, beide sind hier
         * bekannt — die Töchter stehen eine Bildschirmhöhe weiter unten).
         *
         * Tiefere Nachfahren bleiben in der Liste: Wer unter einer Tochter hängt, weiss
         * allein der Server, und ein halb geratener Filter wäre schlechter als seine
         * Meldung am Feld. Der Riegel ist und bleibt dort.
         */
        const toechterIds = new Set((toechter ?? []).map((t) => t.id));
        setTreffer(items.filter((k) => k.id !== kundeId && !toechterIds.has(k.id)));
      } catch {
        if (meiner !== lauf.current) return;
        setTreffer([]);
      } finally {
        if (meiner === lauf.current) setSucheLaeuft(false);
      }
    })();
  }, [getCustomers, kundeId, suche, panelOffen, toechter]);

  const geaendert = panelOffen && gewaehlt !== null;
  // Auch das ECHTE Verlassen (Tab zu, neu laden) fragt nach — wie an jeder anderen Maske.
  useBeforeUnload(geaendert);

  /**
   * Elternteil setzen oder entfernen — und danach die eigene Sicht nachziehen.
   *
   * ZWEI Wege und nicht einer: Ist der PATCH durch, IST er durch — das Panel geht zu und
   * der frische Kundensatz geht nach oben. Ein Nachladen, das danach scheitert, darf
   * daran nichts mehr ändern; es bekommt seine eigene Bandmeldung. Lägen beide in
   * demselben `try`, landete der Befund des Nachladens im Feldfehler eines Panels, das
   * längst zu ist: Der Kopf zeigte den neuen Elternteil, der Reiter die alte Kette, und
   * gesagt hätte es niemand (Review Aufgabe 5, B1).
   */
  async function schreibe(parentId: string | null): Promise<void> {
    const meiner = lauf.current;
    const imPanel = panelOffen;
    setArbeitet(true);
    setMeldung(null);
    setFeldFehler(null);

    let frisch: KundeAkte;
    try {
      frisch = await client.setCustomerParent(kundeId, kunde.version, parentId);
    } catch (fehler) {
      // Der Riegel fällt in JEDEM Fall — auch bei einem verworfenen Lauf (der Kunde hat
      // gewechselt): Ein `arbeitet`, das stehen bleibt, sperrt beide Ausgänge.
      setArbeitet(false);
      if (meiner !== lauf.current) return;
      zeigeFehler(fehler, imPanel);
      return;
    }
    if (meiner !== lauf.current) { setArbeitet(false); return; }

    setPanelOffen(false);
    setGewaehlt(null);
    setSuche("");
    setTreffer([]);
    setFeldFehler(null);
    // Der neue Kundensatz nach oben (Kopf, Kundenliste) — er ist da, unabhängig davon,
    // was das Nachladen gleich sagt.
    p.onAktualisiert(frisch);
    if (parentId === null) fokusNachEntfernen.current = true;

    try {
      const sicht = await getKonzern(kundeId);
      if (meiner !== lauf.current) return;
      setKette(sicht.kette);
      setToechter(sicht.toechter);
    } catch {
      if (meiner !== lauf.current) return;
      setMeldung("Der Konzern konnte nicht neu geladen werden — bitte den Reiter neu "
        + "aufschlagen.");
    } finally {
      setArbeitet(false);
    }
  }

  /**
   * Jeder Befund wird sichtbar — im Panel am Feld, sonst als Band über dem Reiter.
   *
   * Die drei, die von hier aus kommen können, sagt der Server im Klartext: KONZERN_KREIS
   * („das ergäbe einen Kreis"), KONZERN_UNGUELTIG (Kunde gibt es nicht, ist archiviert
   * oder nicht einsehbar) und der Sonderfall F1.4 — ein BESTEHENDER nicht einsehbarer
   * Elternteil muss erst entfernt werden. Deshalb steht hier keine eigene Übersetzung:
   * Die Meldung des Servers ist genauer als jede Ersatzmeldung, die diese Datei erfände.
   */
  function zeigeFehler(fehler: unknown, imPanel: boolean): void {
    if (fehler instanceof ApiError && fehler.status === 409) {
      // Der Kunde wurde zwischenzeitlich geändert. Der Rumpf trägt seinen aktuellen
      // Stand — damit steht die Akte sofort wieder richtig da, und der zweite Versuch
      // geht mit der neuen Version los statt wieder in dieselbe 409.
      const frisch = alsAkte(fehler.conflict?.current);
      if (frisch !== null) p.onAktualisiert(frisch);
      const text = "Dieser Kunde wurde zwischenzeitlich geändert. Der aktuelle Stand "
        + "steht wieder da — bitte erneut versuchen.";
      if (imPanel) setFeldFehler(text); else setMeldung(text);
      return;
    }
    if (fehler instanceof ApiError) {
      const feld = (fehler.fields ?? []).find((f) => f.field === "parentId");
      const text = feld?.message ?? fehler.message;
      if (imPanel) setFeldFehler(text); else setMeldung(text);
      return;
    }
    const text = "Der übergeordnete Kunde konnte nicht gespeichert werden.";
    if (imPanel) setFeldFehler(text); else setMeldung(text);
  }

  /**
   * Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel"): kein Ausgang
   * unter laufendem Schreiben, Rückfrage bei getroffener Wahl. Knopf und Escape gehen
   * durch diese Funktion.
   */
  function abbrechen(): void {
    if (arbeitet) return;
    if (!darfAbbrechen(geaendert)) return;
    setPanelOffen(false);
    setGewaehlt(null);
    setSuche("");
    setTreffer([]);
    setFeldFehler(null);
  }

  const eltern = kunde.parent ?? null;

  function entfernen(): void {
    /*
     * Die Rückfrage sagt, was danach WEG ist — nicht bloss „wirklich entfernen?". Mit dem
     * Elternteil fallen die geerbten Ansprechpartner und Adressen aus dieser Akte, und der
     * Rückfall für eine Rolle findet oben nichts mehr.
     */
    const name = eltern?.companyName ?? "der übergeordnete Kunde";
    const sicher = window.confirm(
      `Zuordnung lösen: ${kunde.companyName} gehört danach zu keinem Konzern mehr `
      + `(bisher: ${name}). Geerbte Ansprechpartner und Adressen verschwinden aus dieser `
      + "Akte. Wirklich lösen?");
    if (!sicher) return;
    void schreibe(null);
  }

  const geladen = kette !== null && toechter !== null;
  /*
   * F1.4: Einen BESTEHENDEN, nicht einsehbaren Elternteil kann niemand still überschreiben
   * — der Server wiese das mit 422 ab. Statt in diese Meldung laufen zu lassen, steht der
   * Weg hier von vornherein da: erst entfernen, dann neu wählen.
   */
  const elternVerdeckt = eltern !== null && (!eltern.sichtbar || eltern.id === null);

  return (
    <section className="kkz" aria-label="Konzern">
      {meldung !== null && <div className="kkz__meldung" role="alert">{meldung}</div>}
      {ladeFehler !== null && (
        <div className="kkz__meldung" role="alert">
          <span>{ladeFehler}</span>
          <button type="button" className="kkz__knopf"
            onClick={() => setLadeVersuch((v) => v + 1)}>
            Erneut versuchen
          </button>
        </div>)}

      {/*
        * Die Aktionsleiste steht UNABHÄNGIG vom Ladezustand da (Review Aufgabe 5, B4): Ob
        * es einen übergeordneten Kunden gibt, sagt `kunde.parent` aus der Akte — nicht die
        * Kette. Scheitert das Laden, wäre die Akte sonst in genau dem Zustand gefangen,
        * den F1 ausdrücklich lösbar halten will.
        */}
      <div className="kkz__aktionen">
        <button type="button" className="kkz__knopf" ref={waehlenRef}
          disabled={arbeitet || panelOffen || elternVerdeckt}
          onClick={() => {
            setPanelOffen(true);
            setGewaehlt(null);
            setSuche("");
            setTreffer([]);
            setFeldFehler(null);
            setMeldung(null);
          }}>
          Übergeordneten Kunden wählen
        </button>
        {/* Auch bei einem NICHT einsehbaren Elternteil (F1): Wer ihn nicht sieht, müsste
            sonst mit einer Zuordnung leben, die niemand mehr lösen kann. */}
        {eltern !== null && (
          <button type="button" className="kkz__knopf"
            disabled={arbeitet || panelOffen}
            onClick={entfernen}>
            Übergeordneten Kunden entfernen
          </button>
        )}
      </div>
      {elternVerdeckt && (
        <p className="kkz__hinweis">
          Erst den bestehenden, nicht einsehbaren Elternteil entfernen.
        </p>
      )}

      {!geladen && ladeFehler === null && (
        <p className="kkz__hinweis" role="status">Lädt …</p>)}

      {geladen && (
        <>
          {kette.length === 0 && toechter.length === 0 && (
            <p className="kkz__hinweis">
              {kunde.companyName} gehört zu keinem Konzern — kein übergeordneter Kunde,
              keine Töchter.
            </p>
          )}

          {kette.length > 0 && (
            <div className="kkz__block">
              <h3 className="kkz__titel">Übergeordnete Kunden</h3>
              <ul className="kkz__liste" aria-label="Übergeordnete Kunden">
                {kette.map((k, i) => (
                  <li key={k.id ?? `verdeckt-${i}`} className="kkz__zeile">
                    <KnotenZeile knoten={k} stufe={i + 1} archivMarke="Eltern archiviert"
                      onKunde={p.onKunde} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {toechter.length > 0 && (
            <div className="kkz__block">
              <h3 className="kkz__titel">Töchter</h3>
              <ul className="kkz__liste" aria-label="Töchter">
                {toechter.map((t, i) => (
                  <li key={t.id ?? `verdeckt-${i}`} className="kkz__zeile">
                    <KnotenZeile knoten={t} stufe={null} archivMarke="Archiviert"
                      onKunde={p.onKunde} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {panelOffen && (
        <Modal titel="Übergeordneten Kunden wählen" onAbbrechen={abbrechen}
          abbrechenGesperrt={arbeitet} breite="mittel">
          <div className="kkz__dialoginhalt">
            <p className="kkz__dialogtext">
              {kunde.companyName} wird dem gewählten Kunden untergeordnet. Die
              Ansprechpartner und Adressen des Konzerns erscheinen danach auch hier —
              geändert werden sie weiterhin nur beim Ursprungskunden.
            </p>
            {feldFehler !== null && (
              <p className="kkz__feldfehler" role="alert">{feldFehler}</p>)}
            <div className="kkz__kundenwahl">
              {/* Zur Wahl stehen nur EINSEHBARE Kunden (F1, Punkt 4) — das leistet
                  `getCustomers` von sich aus, es filtert über die sichtbaren
                  Unternehmen. */}
              <KundenListe
                // Eigene Feld-Id: Die Kundenliste steht auch in der linken Spalte, und
                // zwei Elemente mit derselben DOM-Id im Dokument sind eines zu viel.
                feldId="kkz-suchfeld"
                items={treffer}
                loading={sucheLaeuft}
                error={null}
                search={suche}
                onSearch={(neueSuche) => {
                  // Mit einer neuen Suche fällt die alte Wahl. Sonst stünde sie nur noch
                  // unsichtbar im Zustand — und „Speichern" ordnete einem Kunden den
                  // Elternteil zu, der gerade gar nicht mehr auf dem Bildschirm steht.
                  setSuche(neueSuche);
                  setGewaehlt(null);
                  setFeldFehler(null);
                }}
                selectedId={gewaehlt?.id ?? null}
                onSelect={(id) => {
                  setFeldFehler(null);
                  setGewaehlt(treffer.find((k) => k.id === id) ?? null);
                }}
                companies={p.companies ?? []}
              />
            </div>
          </div>
          {/* Die zwei einzigen Ausgänge. `abbrechenGesperrt` steht an BEIDEN Stellen — am
              Rahmen für die Escape-Taste, hier für den Knopf. */}
          <ModalFuss
            speichernText="Speichern"
            speichernGesperrt={gewaehlt === null || arbeitet}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { if (gewaehlt !== null) void schreibe(gewaehlt.id); }}
            onAbbrechen={abbrechen}
          />
        </Modal>
      )}
    </section>
  );
}

/**
 * Eine Zeile der Kette oder der Töchterliste.
 *
 * Ein nicht einsehbarer Knoten (Festlegung F1) ist KEIN Knopf: Er hat kein Ziel, und ein
 * Knopf, der auf „nicht gefunden" führte, wäre schlimmer als ein Text.
 *
 * Der zugängliche Name trägt ALLES, was die Zeile sichtbar sagt — Stufe und Archivmarke
 * eingeschlossen. Ein `aria-label` ersetzt den Inhalt des Knopfes, es ergänzt ihn nicht:
 * Stünde dort nur der Firmenname, verschwiegen die Zeilen für einen Screenreader genau
 * die beiden Angaben, um derentwillen sie gezeichnet werden (Review Aufgabe 5, B2). Er
 * endet auf „anzeigen" (Wortliste der Zeilenlisten).
 */
function KnotenZeile(p: {
  knoten: KonzernKnoten;
  /** Die Stufe in der Kette (1 = unmittelbarer Elternteil); `null` bei einer Tochter. */
  stufe: number | null;
  /** Was an einem archivierten Knoten steht — oben „Eltern archiviert", unten schlicht
   *  „Archiviert": Dieselbe Marke mit demselben Wortlaut wäre an einer Tochter falsch. */
  archivMarke: string;
  onKunde: (id: string) => void;
}): ReactElement {
  const id = p.knoten.id;
  if (!p.knoten.sichtbar || id === null) {
    // Nur in der KETTE: Die Töchterliste schickt gar keine verdeckten Knoten mit (der
    // Server lässt sie fort, F1) — eine zweite Zeile für einen Fall, den es dort nicht
    // gibt, wäre toter Text.
    return (
      <span className="kkz__verdeckt">Übergeordneter Kunde — nicht einsehbar</span>
    );
  }
  const stufenMarke = p.stufe !== null && p.stufe > 1 ? `${p.stufe}. Stufe` : null;
  const zusatz = (stufenMarke === null ? "" : `, ${stufenMarke}`)
    + (p.knoten.archiviert ? `, ${p.archivMarke}` : "");
  return (
    <button type="button" className="kkz__eintrag"
      aria-label={`${p.knoten.companyName}${zusatz}, anzeigen`}
      /* Der Sprung führt in die Akte des Knotens — und nimmt bewusst KEINE Herkunft mit:
         Der andere Kunde ist eine Ebene für sich, kein Unterpunkt dieses Reiters. Wer
         zurückwill, geht den Weg, den er gekommen ist (Zurück-Taste); ein mitgereister
         Rückweg behauptete eine Klammer, die es nicht gibt. */
      onClick={() => p.onKunde(id)}>
      <span className="kkz__name">{p.knoten.companyName}</span>
      {stufenMarke !== null && <span className="kkz__marke">{stufenMarke}</span>}
      {p.knoten.archiviert && (
        <span className="kkz__marke kkz__marke--warnung">{p.archivMarke}</span>)}
    </button>
  );
}
